import React from 'react';
import { DateTime, Duration } from 'luxon';

import DA from '../store/ReduxAccess';

const missiveType2ActivityModeMap = new Map([
  ['PersonalEmail', 'email'],
  ['PersonalVoiceMailOrMessage', 'voicemail'],
  ['PhoneCall', 'phone'],
  ['PersonalSMS', 'text'],
  ['MassEmail', 'email'],
  ['MassVoiceMail', 'voicemail'],
  ['MassSMS', 'text'],
  ['Newsletter', 'email'],
  ['MassMailing', 'post'],
  ['Meeting', 'meeting'],
  ['Tour', 'tour'],
  ['ScheduleAssessment', 'scheduleAssessment'],
  ['CompleteAssessment', 'completeAssessment'],
]);

export const outreachRepForDisplay = (outreach) => {
  /*
    want an object with:
      id,
      dateTime,
      activityType,
      activityMode,
      direction,
      agentName,
      agentInitials,
      subjectId,
      subjectName,
      subjectType,
      content,
      missiveId,
      sendingOrgId,
      receivingOrgId,
    */
  const sender = DA.getPersonById(outreach.senderId);
  const agentName = sender ? sender.preferredFullName : 'Unknown';
  const agentId = outreach.senderId;
  const agentInitials = extractInitialsFromFullName(agentName);
  let subjectId = 0;
  let subjectName = '';
  let subjectType = '';
  if (outreach.receiverId) {
    subjectId = outreach.receiverId;
    subjectName = DA.getPersonById(subjectId).preferredFullName;
    subjectType = 'orgContact';
  } else if (outreach.receiverFirmId) {
    subjectId = outreach.receiverFirmId;
    subjectName = DA.getFirmById(subjectId).name;
    subjectType = 'org';
  } else if (outreach.dlId) {
    subjectId = outreach.dlId;
    subjectName = DA.getDistributionListById(subjectId).name;
    subjectType = 'dl';
  }
  const missive = DA.getMissiveById(outreach.missiveId) || {};
  const typeStr = (missive.typeStr ? missive.typeStr : missive.missiveType) || '';
  const isMeetingType = typeStr === 'Meeting' || typeStr === 'Tour' || typeStr.endsWith('Assessment');
  const activityType = isMeetingType ? 'meeting' : typeStr.indexOf('SMS') >= 0 ? 'correspondence' : 'note';
  const activityMode = missiveType2ActivityModeMap.get(typeStr);
  let receivingOrgId = outreach.receiverFirmId;
  let sendingOrgId = outreach.firmId;
  let direction = 'To';
  if (outreach.firmId === outreach.receiverFirmId) {
    direction = 'From';
    receivingOrgId = outreach.firmId;
    sendingOrgId = outreach.senderFirmId;
  }
  return {
    id: outreach.outreachId,
    dateTime: outreach.date,
    activityType,
    activityMode,
    direction: direction,
    agentId: agentId,
    agentName: agentName,
    agentInitials: agentInitials,
    subjectId: subjectId,
    subjectName: subjectName,
    subjectType: subjectType,
    content: missive.content,
    missiveId: outreach.missiveId,
    sendingOrgId,
    receivingOrgId,
  };
};

export const extractInitialsFromFullName = (fullName) => {
  const firstLetter = fullName[0];
  const lastSpaceLoc = fullName.lastIndexOf(' ');
  const otherLetter = fullName[lastSpaceLoc + 1];
  return firstLetter + otherLetter;
};

export const transitionRepForDisplayAsActivity = (transition) => {
  const subjectId = transition.subjectId;
  const client = DA.getPersonById(subjectId);
  const subjectName = client.preferredFullName;
  const agentName = '';
  const agentInitials = 's S';
  return {
    id: transition.transitionId || transition.statusTransitionId,
    dateTime: transition.date,
    activityType: 'statusTransition',
    activityMode: 'to' + transition.newStatus,
    agentName: agentName,
    agentInitials: agentInitials,
    subjectId: subjectId,
    subjectName: subjectName,
    subjectType: 'client',
    content: transition.transitionDescrip,
  };
};

const extractAgentNameFromNote = (note) => {
  const agent = DA.getPersonById(note.authorId);
  if (agent) {
    return agent.preferredFullName;
  }
  if (note.content.startsWith('Contacted by prospect')) {
    return DA.getPersonById(note.subjectId).preferredFullName;
  }
  if (note.content.startsWith('Contacted by emergency contact, ')) {
    const rest = note.content.substring('Contacted by emergency contact, '.length);
    const endOfName = rest.indexOf(',');
    return rest.substring(0, endOfName);
  }
  return 'Unknown Author';
};

export const noteRepForDisplayAsActivity = (note) => {
  const subjectId = note.subjectId;
  const agentName = extractAgentNameFromNote(note);
  const agentInitials = extractInitialsFromFullName(agentName);
  const isForFirm = subjectId === 0;
  if (isForFirm) {
    const firmId = note.otherFirmId;
    const firm = DA.getFirmById(firmId);
    const firmName = firm.name;
    return {
      id: note.noteId,
      dateTime: note.date,
      activityType: 'note',
      activityMode: 'note',
      agentName: agentName,
      agentInitials: agentInitials,
      subjectId: firmId,
      subjectName: firmName,
      subjectType: 'firm',
      content: note.content,
    };
  } else {
    const client = DA.getPersonById(subjectId);
    const subjectName = client.preferredFullName;
    return {
      id: note.noteId,
      dateTime: note.date,
      activityType: 'note',
      activityMode: 'note',
      agentName: agentName,
      agentInitials: agentInitials,
      subjectId: subjectId,
      subjectName: subjectName,
      subjectType: 'client',
      content: note.content,
    };
  }
};

export const sortIntoActivityGroups = (outreaches) => {
  const now = DateTime.local();
  const minusOneDay = Duration.fromObject({ days: -1 });
  const startOfToday = now.startOf('day');
  const startOfYesterday = startOfToday.plus(minusOneDay);
  const startOfDBYesterday = startOfYesterday.plus(minusOneDay);
  const startOfThisWeek = now.startOf('week');
  const startOfLastWeek = startOfThisWeek.plus(minusOneDay).startOf('week');
  const startOfThisMonth = now.startOf('month');
  const startOfLastMonth = startOfThisMonth.plus(minusOneDay).startOf('month');

  const todayActivities = [];
  const yesterdayActivities = [];
  const dbYesterdayActivities = [];
  const thisWeekActivities = [];
  const lastWeekActivities = [];
  const thisMonthActivities = [];
  const lastMonthActivities = [];
  const earlierActivities = [];

  const filteredOutreaches = outreaches.filter((o) => o.activityMode !== 'toNone');

  const remainder = filteredOutreaches.sort((o1, o2) => o2.dateTime - o1.dateTime);
  remainder.forEach((o) => {
    if (o.dateTime.valueOf() >= startOfToday.valueOf()) {
      todayActivities.push(o);
    } else if (
      startOfYesterday.valueOf() >= startOfThisWeek.valueOf() &&
      o.dateTime.valueOf() > startOfYesterday.valueOf()
    ) {
      yesterdayActivities.push(o);
    } else if (
      startOfDBYesterday.valueOf() >= startOfThisWeek.valueOf() &&
      o.dateTime.valueOf() >= startOfDBYesterday.valueOf()
    ) {
      dbYesterdayActivities.push(o);
    } else if (
      startOfThisWeek.valueOf() >= startOfThisMonth.valueOf() &&
      o.dateTime.valueOf() >= startOfThisWeek.valueOf()
    ) {
      thisWeekActivities.push(o);
    } else if (
      startOfLastWeek.valueOf() >= startOfThisMonth.valueOf() &&
      o.dateTime.valueOf() >= startOfLastWeek.valueOf()
    ) {
      lastWeekActivities.push(o);
    } else if (o.dateTime.valueOf() >= startOfThisMonth.valueOf()) {
      thisMonthActivities.push(o);
    } else if (o.dateTime.valueOf() >= startOfLastMonth.valueOf()) {
      lastMonthActivities.push(o);
    } else {
      earlierActivities.push(o);
    }
  });

  const groups = [];
  if (todayActivities.length > 0) {
    groups.push({
      label: 'today',
      activities: todayActivities,
    });
  }
  if (yesterdayActivities.length > 0) {
    groups.push({
      label: 'yesterday',
      activities: yesterdayActivities,
    });
  }
  if (dbYesterdayActivities.length > 0) {
    groups.push({
      label: 'day before yesterday',
      activities: dbYesterdayActivities,
    });
  }
  if (thisWeekActivities.length > 0) {
    groups.push({
      label: 'this week',
      activities: thisWeekActivities,
    });
  }
  if (lastWeekActivities.length > 0) {
    groups.push({
      label: 'last week',
      activities: lastWeekActivities,
    });
  }
  if (thisMonthActivities.length > 0) {
    groups.push({
      label: 'this month',
      activities: thisMonthActivities,
    });
  }
  if (lastMonthActivities.length > 0) {
    groups.push({
      label: 'last month',
      activities: lastMonthActivities,
    });
  }
  if (earlierActivities.length > 0) {
    groups.push({
      label: 'earlier',
      activities: earlierActivities,
    });
  }
  return groups;
};

export const createFakeActivityGroups = () => {
  const nGroups = 5;
  let retVal = [];
  for (let i = 0; i < nGroups; i++) {
    retVal = retVal.concat(createFakeActivityGroup(i));
  }
  return retVal;
};

function createFakeActivityGroup(groupNum) {
  const nInGroup = Math.pow(2, groupNum);
  const retObj = {
    label: `Group ${groupNum + 1}`,
    activities: [],
  };
  for (let i = 0; i < nInGroup; i++) {
    retObj.activities.push(createFakeActivity());
  }
  return retObj;
}

let activityNum = 0;

function createFakeActivity() {
  activityNum++;

  const getRandomActivityTypeAndMode = () => {
    const reasonablePairs = [
      {
        activityType: 'note',
        activityMode: 'email',
      },
      {
        activityType: 'note',
        activityMode: 'phone',
      },
      {
        activityType: 'note',
        activityMode: 'voicemail',
      },
      {
        activityType: 'correspondence',
        activityMode: 'text',
      },
      {
        activityType: 'correspondence',
        activityMode: 'email',
      },
      {
        activityType: 'none',
        activityMode: 'none',
      },
    ];
    return reasonablePairs[Math.floor(Math.random() * reasonablePairs.length)];
  };

  const getRandomName = () => {
    const firstNames = ['Bob', 'Carol', 'Ted', 'Alice', 'Manny', 'Moe', 'Joe', 'Georgia', 'Florence', 'Clara'];
    const lastNames = [
      'Jones',
      'King',
      'Kaehler',
      'Waters',
      'Mota',
      'Schmo',
      'Morgan',
      "O'Keefe",
      'Nightingale',
      'Barton',
    ];
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    return firstName + ' ' + lastName;
  };

  const getRandomContent = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz';

    const selectRandomCharacter = () => {
      const i = Math.floor(Math.random() * chars.length);
      return chars[i];
    };

    const generateWord = () => {
      const nChars = Math.floor(Math.random() * 6) + 2;
      let word = '';
      for (let i = 0; i < nChars; i++) {
        word = word + selectRandomCharacter();
      }
      return word;
    };

    const generateRandomText = () => {
      const nWords = Math.floor(Math.random() * 24) + 4;
      const words = [];
      for (let i = 0; i < nWords; i++) {
        words.push(generateWord());
      }
      return words.join(' ');
    };

    const nPs = Math.floor(Math.random() * 3) + 1;
    const ps = [];
    for (let i = 0; i < nPs; i++) {
      ps.push(<p key={i}>{generateRandomText()}</p>);
    }
    return ps;
  };

  const id = Math.floor(Math.random() * 10000);
  const now = DateTime.local();
  const delta = Duration.fromObject({ days: activityNum });
  const dateTime = now.minus(delta);
  const { activityType, activityMode } = getRandomActivityTypeAndMode();
  const agentName = getRandomName();
  const firstLetter = agentName[0];
  const lastSpaceLoc = agentName.lastIndexOf(' ');
  const otherLetter = agentName[lastSpaceLoc + 1];
  const agentInitials = firstLetter + otherLetter;
  const subjectId = Math.floor(Math.random() * 10000);
  const subjectName = getRandomName();
  const subjectType = 'orgContact';
  const content = getRandomContent();
  return {
    id,
    dateTime,
    activityType,
    activityMode,
    agentName,
    agentInitials,
    subjectId,
    subjectName,
    subjectType,
    content,
  };
}
